// A question a chat put to the person (lib/chatQuestion.js), answered in place:
// pick any number of the choices and Answer, ask for other options, or type an
// answer in the chat.
import { createContext, useContext, useEffect, useState } from "react";
import { answerFrom, otherOptionsFrom } from "../lib/chatQuestion.js";
import { FILE_LABEL } from "./lenses/triageParts.jsx";

// How a card in the transcript answers in the chat: `reply(text)` sends it as
// the person's next message; `busy` while the chat is still working.
// `answered(nodeId)` says a question already has the person's reply after it.
export const ChatReplyContext = createContext({ reply: null, busy: false, answered: null });

export const CARD_BUTTON = "cursor-pointer rounded-[2px] border border-[color:var(--line-strong)] bg-transparent px-[8px] py-[2px] text-[11px] leading-[16px] text-[color:var(--ink)] transition-colors hover:border-[color:var(--ink)] disabled:cursor-default disabled:opacity-40";
export const CARD_PICKED = "border-[color:var(--ink)] !bg-[color:var(--control-bg)]";
export const CARD_MUTED = "text-[11px] leading-[1.4] text-[color:var(--muted)]";
export const CARD = "grid gap-[6px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel)] px-[12px] py-[10px]";

// What a card was left at, per chat node, so switching chats and coming back
// keeps what was picked or done this visit.
const kept = new Map();
export function useKept(key, initial) {
    const [state, setState] = useState(() => kept.get(key) || initial);
    useEffect(() => { if (key) kept.set(key, state); }, [key, state]);
    return [state, setState];
}

export default function ChatQuestionCard({ nodeId, details }) {
    const { reply, busy, answered } = useContext(ChatReplyContext);
    const [state, setState] = useKept(`${nodeId}:q`, { picks: [], sent: false });
    const choices = Array.isArray(details?.choices) ? details.choices : [];
    const done = state.sent || Boolean(answered?.(nodeId));
    const locked = done || busy || !reply;
    const toggle = (choice) => {
        if (locked) return;
        setState((s) => ({ ...s, picks: s.picks.includes(choice) ? s.picks.filter((p) => p !== choice) : [...s.picks, choice] }));
    };
    // In the order offered, whatever order they were clicked in.
    const picked = () => choices.filter((c) => state.picks.includes(c));
    const send = (text) => {
        if (locked) return;
        setState((s) => ({ ...s, sent: true }));
        reply(text);
    };
    // With the card focused, a choice's number picks it and Enter answers.
    const onKey = (e) => {
        if (locked || e.target !== e.currentTarget) return;
        const n = Number(e.key);
        if (Number.isInteger(n) && n >= 1 && n <= choices.length) { e.preventDefault(); toggle(choices[n - 1]); }
        else if (e.key === "Enter" && state.picks.length) { e.preventDefault(); send(answerFrom(picked())); }
    };
    return (
        <div data-chat-question="" tabIndex={locked ? undefined : 0} onKeyDown={onKey} className={`${CARD} outline-none focus-visible:border-[color:var(--ink)]`}>
            <span className={FILE_LABEL}>{done ? "Question \u00b7 answered" : choices.length ? "Question \u00b7 pick any" : "Question"}</span>
            <span className="text-[13px] leading-[1.4] text-[color:var(--ink)]">{details?.question}</span>
            {choices.length ? (
                <span className="grid">
                    {choices.map((c, i) => {
                        const on = state.picks.includes(c);
                        return (
                            <button
                                key={c}
                                type="button"
                                data-chat-choice={c}
                                aria-pressed={on}
                                disabled={locked}
                                onClick={() => toggle(c)}
                                className={`flex w-full cursor-pointer items-baseline gap-x-[8px] border-0 border-t border-solid border-[color:var(--line-soft)] bg-transparent px-[4px] py-[5px] text-left text-[12px] leading-[1.35] transition-colors hover:bg-[color:var(--control-bg)] disabled:cursor-default ${on ? "!bg-[color:var(--control-bg)] text-[color:var(--ink)]" : locked ? "text-[color:var(--muted)]" : "text-[color:var(--ink)]"}`}
                            >
                                <span className="w-[12px] shrink-0 font-mono text-[10px] text-[color:var(--muted-soft)]">{i + 1}</span>
                                <span className="min-w-0 flex-1">{c}</span>
                                {on ? <span aria-hidden="true" className="shrink-0 text-[11px] text-[color:var(--ink)]">{"\u2713"}</span> : null}
                            </button>
                        );
                    })}
                </span>
            ) : null}
            {done ? null : (
                <span className="flex flex-wrap items-center gap-[8px] border-t border-[color:var(--line-soft)] pt-[6px]">
                    {choices.length ? (
                        <>
                            <button type="button" data-chat-answer="" disabled={locked || !state.picks.length} onClick={() => send(answerFrom(picked()))} className={`${CARD_BUTTON} ${CARD_PICKED}`}>
                                Answer{state.picks.length > 1 ? ` ${state.picks.length}` : ""}
                            </button>
                            <button type="button" data-chat-other-options="" disabled={locked} onClick={() => send(otherOptionsFrom(picked()))} className={`${CARD_BUTTON} border-transparent`}>
                                Other options
                            </button>
                        </>
                    ) : null}
                    <span className={`${CARD_MUTED} ml-auto`}>{choices.length ? "Pick by number, or type your own below" : "Type your answer below."}</span>
                </span>
            )}
        </div>
    );
}
